<template>
  <div class="review-requirements">
    <!-- 标题 -->
    <div class="review-requirements-header">
      <div class="review-requirements-header-title">配置审查要求</div>
      <div class="review-requirements-header-desc">
        请选择审查立场、审查尺度，并填写重点审查内容。配置将直接影响风险识别与修改建议。
      </div>
    </div>

    <!-- 主卡片（a-spin：数据统一走接口后为异步加载） -->
    <a-spin :spinning="detailLoading">
      <div class="config-card">
        <!-- 合同文件 -->
        <div class="card-row">
          <div class="card-row-left">
            <span class="card-row-label">合同文件</span>
            <span class="card-row-desc"
              >系统已完成基础解析，可重新上传或继续配置审查要求。</span
            >
          </div>
          <a-button @click="handleReupload">重新上传</a-button>
        </div>

        <div class="file-box">
          <div class="file-icon">{{ contractFile.iconLetter }}</div>
          <div class="file-info">
            <div class="file-name">{{ contractFile.name }}</div>
            <div class="file-meta">{{ contractFile.meta }}</div>
          </div>
          <div class="file-status">已解析</div>
        </div>

        <!-- 合同状态 -->
        <div class="draft-status-row">
          <div class="draft-status-copy">
            <div class="section-title">合同状态</div>
            <div class="section-desc">请选择当前合同状态。</div>
          </div>
          <div
            class="draft-status-options"
            role="radiogroup"
            aria-label="合同状态"
          >
            <button
              class="draft-option"
              :class="{ active: !isDraft }"
              type="button"
              :aria-pressed="(!isDraft).toString()"
              @click="chooseDraftStatus(false)"
            >
              <span class="draft-radio"></span>
              <span class="draft-option-content">
                <span class="draft-option-title">正式合同</span>
                <span class="draft-option-desc">按正常流程审查。</span>
              </span>
            </button>
            <button
              class="draft-option"
              :class="{ active: isDraft }"
              type="button"
              :aria-pressed="isDraft.toString()"
              @click="chooseDraftStatus(true)"
            >
              <span class="draft-radio"></span>
              <span class="draft-option-content">
                <span class="draft-option-title">草稿合同</span>
                <span class="draft-option-desc"
                  >尚未定稿，跳过有效性审查。</span
                >
              </span>
            </button>
          </div>
        </div>

        <!-- 审查立场 -->
        <div class="card-row">
          <div class="card-row-left">
            <span class="card-row-label">审查立场</span>
            <span class="card-row-desc"
              >请选择你希望 AI 代表哪一方进行审查。</span
            >
          </div>
          <span class="right-tag right-tag-required">必选</span>
        </div>

        <div class="stance-group">
          <div
            v-for="item in stanceOptions"
            :key="item.key"
            class="stance-item"
            :class="{ active: selectedStance === item.key }"
            @click="selectedStance = item.key"
          >
            <div class="stance-title">{{ item.title }}</div>
            <div class="stance-company">{{ item.company }}</div>
            <div class="stance-hint">{{ item.hint }}</div>
          </div>
        </div>

        <!-- 审查尺度 -->
        <div class="card-row">
          <div class="card-row-left">
            <span class="card-row-label">审查尺度</span>
            <span class="card-row-desc"
              >强势更保护我方，均势更平衡，弱势只提示重大风险。</span
            >
          </div>
          <span class="right-tag">默认均势</span>
        </div>

        <div class="scale-group">
          <div
            v-for="item in scaleOptions"
            :key="item.key"
            class="scale-item"
            :class="{ active: selectedScale === item.key }"
            @click="selectedScale = item.key"
          >
            <div class="scale-title">{{ item.title }}</div>
            <div class="scale-desc">{{ item.desc }}</div>
          </div>
        </div>

        <!-- 审阅人与自定义要求 -->
        <div class="section-block">
          <span class="section-label">审阅人与自定义要求</span>
          <span class="section-desc"
            >审阅人会展示在批注中；自定义要求用于告诉 AI 重点审查范围。</span
          >

          <span class="input-label">审阅人</span>
          <a-input
            v-model:value="reviewer"
            class="custom-input"
            placeholder="请输入审阅人"
          />

          <span class="input-label input-label-mt">自定义审查要求</span>
          <a-textarea
            v-model:value="customRequirement"
            class="custom-textarea"
            :rows="4"
            :maxlength="500"
            show-count
            placeholder="请输入自定义审查要求..."
          />
        </div>

        <!-- 参考文件 -->
        <div class="section-block section-block-mt">
          <span class="section-label">参考文件</span>
          <span class="section-desc"
            >可上传报价单、招投标文件、补充协议、历史合同版本等，用于辅助判断。</span
          >
          <div class="ref-box">
            <div class="ref-left">
              <strong>上传参考文件</strong>
              <span class="ref-hint"
                >支持多文件上传，可在审查过程中一起读取</span
              >
            </div>
            <a-upload
              :multiple="true"
              :show-upload-list="false"
              :custom-request="handleRefUpload"
              accept=".doc,.docx,.pdf,.xlsx,.xls,.png,.jpg,.jpeg"
            >
              <a-button>继续上传</a-button>
            </a-upload>
          </div>

          <!-- 已上传参考文件列表 -->
          <div v-if="referenceFiles.length" class="ref-list" id="refList">
            <div
              v-for="(item, idx) in referenceFiles"
              :key="item.fileId ?? idx"
              class="ref-item"
            >
              <div class="file-ico">{{ item.iconLetter }}</div>
              <div class="file-main" :title="item.name">{{ item.name }}</div>
              <button
                type="button"
                class="icon-btn"
                title="移除"
                @click="removeReference(idx)"
              >
                <DeleteOutlined />
              </button>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="action-row">
          <a-button @click="handleBack">返回</a-button>
          <a-button type="primary" @click="handleStartReview"
            >开始审查</a-button
          >
        </div>
      </div>
    </a-spin>

    <!-- 审查策略卡片 -->
    <div class="strategy-cell">
      <div
        ref="strategySentinelEl"
        class="strategy-sentinel"
        aria-hidden="true"
      ></div>
      <div
        class="strategy-card"
        ref="strategyCardEl"
        :style="strategyCardStyle"
      >
        <div class="strategy-card-title">本次审查策略</div>
        <div class="strategy-item" :class="{ 'is-pending': !selectedStance }">
          <div class="strategy-badge">{{ stanceBadge }}</div>
          <div class="strategy-info">
            <div class="strategy-main">{{ stanceMainText }}</div>
            <div class="strategy-sub">{{ stanceSubText }}</div>
          </div>
        </div>
        <div class="strategy-item">
          <div class="strategy-badge">{{ scaleBadge }}</div>
          <div class="strategy-info">
            <div class="strategy-main">审查尺度：{{ scaleMainText }}</div>
            <div class="strategy-sub">{{ scaleSubText }}</div>
          </div>
        </div>
        <div class="strategy-card-title strategy-card-title-mt">
          AI 将重点检查
        </div>
        <div class="tags">
          <span class="tag tag-default">交易内容</span>
          <span class="tag tag-default">合同价款</span>
          <span class="tag tag-default">权利义务</span>
          <span class="tag tag-risk">实质风险</span>
          <span class="tag tag-symbol">文字符号</span>
          <span class="tag tag-party">主体风险</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import { message } from "ant-design-vue";
import { DeleteOutlined } from "@ant-design/icons-vue";
import {
  uploadReferenceFile,
  getGetDetailById,
  getGetById,
} from "@/api/contractReview";
const router = useRouter();
const route = useRoute();

// 文件名 -> 图标字母（与 index.vue 保持一致）
const getGetByIdIconLetter = (name) => {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (ext === "doc" || ext === "docx") return "W";
  if (ext === "pdf") return "P";
  return "F";
};

// 文件扩展名（小写）
const getFileExt = (name) => {
  const parts = String(name || "").split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

// 文件扩展名（后端字段 fileExtension，如 ".pdf"/".docx"/".doc"）：
// 优先接口返回；缺失时按文件名扩展名兜底，统一带点小写
const resolveFileExtension = (uc) => {
  if (uc?.fileExtension) return String(uc.fileExtension).toLowerCase();
  const ext = getFileExt(uc?.name);
  return ext ? `.${ext}` : "";
};

// 字节数 -> 人类可读（1024 进制，保留 1 位小数）
const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

// 合同文件信息：从上传接口返回的共享状态构造，无则使用占位
const buildContractFile = (uc) => {
  if (!uc) {
    return {
      name: "未选择合同",
      meta: "请先上传合同文件",
      contractId: null,
      fileId: null,
      iconLetter: "W",
    };
  }
  const parts = [];
  if (uc.contractType) parts.push(`识别合同类型：${uc.contractType}`);
  // parties 可能是列表 [{ name, role, stance }]（新结构），也可能是字符串（老结构）
  const partiesText = Array.isArray(uc.parties)
    ? uc.parties
        .map((p) => p?.name)
        .filter(Boolean)
        .join("、")
    : uc.parties;
  if (partiesText) parts.push(`识别双方：${partiesText}`);
  if (uc.wordCount) parts.push(`${uc.wordCount.toLocaleString()} 字`);
  if (uc.fileSize) parts.push(formatFileSize(uc.fileSize));
  return {
    name: uc.name,
    meta: parts.length > 0 ? parts.join("｜") : "解析信息待补全",
    contractId: uc.contractId || null,
    fileId: uc.fileId || null,
    iconLetter: getGetByIdIconLetter(uc.name),
  };
};
/* 数据源（统一）：onMounted 用 query.id 调 getGetDetailById 拉，
 * 回填 sourceData 让 contractFile / stanceOptions 自动重建。
 * 上传后跳入与历史草稿进入走同一条路。 */
const sourceData = ref(null);
// 接口拉取中（a-spin 包住主卡片，避免闪「未选择合同」占位符）
const detailLoading = ref(false);
const contractFile = computed(() => buildContractFile(sourceData.value));

/* 把 getGetDetailById 的返回归一化成 sourceData 的统一形状，
 * 让 buildContractFile / buildStanceOptions 消费。
 * 字段做兜底，方便后端字段调整时不至于直接空。 */
const normalizeDetail = (data, routeId, routeFileId) => {
  // 兼容多种后端字段命名提取双方主体名称（与 index.vue writeSharedUpload 一致：
  // partyA 可能是字符串，也可能是 { name } 对象）
  const partyAObj = data.partyA || data.party_a || {};
  const partyBObj = data.partyB || data.party_b || {};
  const partyAName =
    (typeof partyAObj === "string" ? partyAObj : partyAObj.name) ||
    data.partyAName ||
    data.party_a_name ||
    "";
  const partyBName =
    (typeof partyBObj === "string" ? partyBObj : partyBObj.name) ||
    data.partyBName ||
    data.party_b_name ||
    "";
  return {
    name: data.fileName || data.contractName || data.name || "未命名合同",
    contractType: data.contractType || "",
    partyAName,
    partyBName,
    // parties 新结构：[{ index, name, role, stance }]，可能存在多个甲方/乙方
    parties: Array.isArray(data.parties) ? data.parties : [],
    wordCount: data.wordCount || 0,
    // 文件扩展名（后端返回，如 ".pdf"；缺失时由 resolveFileExtension 按文件名兜底）
    fileExtension: data.fileExtension || "",
    contractId: data.contractId || data.id || routeId,
    fileId: data.fileId || data.file_id || routeFileId,
  };
};

/* 审查立场：后端新结构 parties 是列表 [{ index, name, role, stance }]，
 * 可能存在多个甲方/乙方 —— 全部渲染成卡片，但只能选一个。
 * key 用数组下标保证唯一：后端 parties[].index 是按 stance 分组的序号，
 * 甲方乙方可能同为 0，用它拼 key 会重复（选中一个两个都亮）；
 * stance 才是提交给后端的枚举（PartyA/PartyB）。 */
const PARTY_HINTS = {
  PartyA: "重点关注交付、验收、质量、售后和对方违约责任",
  PartyB: "重点关注付款、验收期限、对方拒收条件和风险转移",
};
const buildStanceOptions = (uc) => {
  const list = Array.isArray(uc?.parties)
    ? uc.parties.filter((p) => p && p.name)
    : [];
  if (list.length) {
    return list.map((p, i) => {
      const stance = p.stance || (p.role === "甲方" ? "PartyA" : "PartyB");
      return {
        key: `party-${i}`,
        stance,
        title: p.role || (stance === "PartyA" ? "甲方" : "乙方"),
        company: p.name,
        hint: PARTY_HINTS[stance] || "",
      };
    });
  }
  // 兜底：老结构（上传流程写入的 partyAName / partyBName）
  return [
    {
      key: "PartyA",
      stance: "PartyA",
      title: "甲方",
      company: uc?.partyAName || "甲方",
      hint: PARTY_HINTS.PartyA,
    },
    {
      key: "PartyB",
      stance: "PartyB",
      title: "乙方",
      company: uc?.partyBName || "乙方",
      hint: PARTY_HINTS.PartyB,
    },
  ];
};
const stanceOptions = computed(() => buildStanceOptions(sourceData.value));
const selectedStance = ref("");

// 当前选中卡片对应的后端 stance 枚举（提交参数 / 策略卡展示用）
const selectedStanceEnum = computed(() => {
  const opt = stanceOptions.value.find((o) => o.key === selectedStance.value);
  return opt ? opt.stance : "";
});

// 选中的具体主体名称（多甲方/多乙方时区分同 stance 的不同公司，传给后端）
const selectedPartyName = computed(() => {
  const opt = stanceOptions.value.find((o) => o.key === selectedStance.value);
  return opt ? opt.company : "";
});

// 审查尺度：key 与后端 scale 枚举一致
const scaleOptions = [
  { key: "Strong", title: "强势", desc: "风险提示更全面，修改建议更强硬。" },
  { key: "Balanced", title: "均势", desc: "兼顾风险控制和合同可签署性。" },
  { key: "Weak", title: "弱势", desc: "只提示重大风险和底线问题。" },
];
const selectedScale = ref("Balanced");

// 合同状态：false=正式合同（默认），true=草稿合同（跳过有效性审查）
const isDraft = ref(false);
const chooseDraftStatus = (val) => {
  isDraft.value = val;
};

// 审阅人 & 自定义要求
/* 审阅人默认填入当前登录用户的手机号（localStorage.user.phone） */
const reviewer = ref(
  (() => {
    try {
      const raw = localStorage.getItem("user");
      if (raw) {
        const u = JSON.parse(raw);
        if (u?.phone) return u.phone;
      }
    } catch {
      /* ignore */
    }
    return "";
  })(),
);
const customRequirement = ref("");

// 已上传的参考文件列表（展示 + 后续随 getOrStartReview 一并带给结果页）
// 每项 { fileId, name, iconLetter }
const referenceFiles = ref([]);

// 策略卡片：审查立场
const stanceBadge = computed(() => {
  if (!selectedStance.value) return "待";
  return selectedStanceEnum.value === "PartyA" ? "甲" : "乙";
});
const stanceMainText = computed(() => {
  if (!selectedStance.value) return "待选择审查立场";
  return selectedStanceEnum.value === "PartyA"
    ? "代表甲方审查"
    : "代表乙方审查";
});
const stanceSubText = computed(() => {
  if (!selectedStance.value) return "请在左侧选择 AI 代表的一方";
  return selectedStanceEnum.value === "PartyA"
    ? "重点保护甲方利益"
    : "重点保护乙方利益";
});

// 策略卡片：审查尺度
const scaleBadge = computed(() => {
  const map = { Strong: "强", Balanced: "均", Weak: "弱" };
  return map[selectedScale.value];
});
const scaleMainText = computed(() => {
  const opt = scaleOptions.find((o) => o.key === selectedScale.value);
  return opt ? opt.title : "";
});
const scaleSubText = computed(() => {
  const map = {
    Strong: "修改建议更强硬，全面提示风险",
    Balanced: "修改建议兼顾风险与可签署性",
    Weak: "只提示重大风险和底线问题",
  };
  return map[selectedScale.value];
});

const handleReupload = () => {
  router.push("/home/contract-review");
};

// 单文件大小上限：50MB
const MAX_FILE_SIZE = 50 * 1024 * 1024;

// 参考文件上传：a-upload 的 customRequest，逐文件调 UploadReferenceFile
const handleRefUpload = async (options) => {
  const { file, onSuccess, onError } = options;
  // 文件大小校验：超过 50MB 直接拦截
  if (file.size > MAX_FILE_SIZE) {
    message.error(`${file.name} 超过 50MB，无法上传`);
    onError(new Error("文件超过 50MB 限制"));
    return;
  }
  const contractId = contractFile.value.contractId;
  if (!contractId) {
    message.warning("请先上传合同");
    onError(new Error("缺少 contractId"));
    return;
  }
  try {
    const res = await uploadReferenceFile(file, contractId);
    const data = (res && (res.data || res)) || {};
    const fid = data.fileId || data.file_id || data.id;
    if (fid != null) {
      referenceFiles.value.push({
        fileId: fid,
        name: file.name,
        iconLetter: getGetByIdIconLetter(file.name),
      });
    }
    onSuccess(res, file);
    message.success(`${file.name} 上传成功`);
  } catch (err) {
    onError(err);
    message.error(`${file.name} 上传失败`);
    console.error("[ReviewRequirements] uploadReferenceFile error:", err);
  }
};

// 移除参考文件（仅前端列表；如需服务端删除可在此扩展）
const removeReference = (idx) => {
  referenceFiles.value.splice(idx, 1);
};

const handleBack = () => {
  router.push("/home/contract-review");
};

const handleStartReview = () => {
  if (!contractFile.value.contractId) {
    message.warning("请先上传合同");
    return;
  }
  if (!selectedStance.value) {
    message.warning("请选择审查立场");
    return;
  }
  if (!selectedScale.value) {
    message.warning("请选择审查尺度");
    return;
  }
  // if (!reviewer.value.trim()) {
  //   message.warning("请填写审阅人");
  //   return;
  // }

  // 把审查参数通过 URL query 带到结果页，结果页 mount 时读取并调 getOrStartReview
  // 走 query 而非共享状态：刷新结果页不丢参，且与最近审查 ?id=xxx 的方式统一
  const query = {
    contractId: contractFile.value.contractId,
    fileId: contractFile.value.fileId,
    stance: selectedStanceEnum.value,
    scale: selectedScale.value,
    // 合同状态随 query 带给结果页（router 会序列化成 "true"/"false" 字符串）
    isDraft: isDraft.value,
  };
  // 文件扩展名随 query 带给结果页（reviewing 态即可判定挂哪个面板）
  const fileExt = resolveFileExtension(sourceData.value);
  if (fileExt) query.fileExtension = fileExt;
  // 参考文件 ID 列表，逗号分隔（修复此前未透传的断链）
  const refIds = referenceFiles.value
    .map((f) => f.fileId)
    .filter((v) => v != null)
    .join(",");
  if (refIds) query.referenceFileIds = refIds;
  // 选中的具体主体名称：多甲方/多乙方时后端靠它区分同 stance 的不同公司
  if (selectedPartyName.value) query.partyName = selectedPartyName.value;
  if (customRequirement.value)
    query.customRequirement = customRequirement.value;
  if (reviewer.value.trim()) query.reviewer = reviewer.value.trim();
  if (contractFile.value.name) query.contractName = contractFile.value.name;
  router.push({ path: "/home/contract-review/result", query });
};

// 策略卡片吸顶：滚动到距屏幕顶部 88px 时固定（header 64 + 间距 24）
// 走 JS 是因为此页面真正滚动的是 window/body（.content-wrapper 是 flex 项且无 min-height:0，
// 不会内部滚动），CSS sticky 只看 .content-wrapper 的滚动，无法激活。
const STRATEGY_STICKY_TOP = 88;
const strategySentinelEl = ref(null);
const strategyCardEl = ref(null);
const strategyCardStyle = ref({});
let isStrategyStuck = false;
let scrollParent = null;

const updateSticky = () => {
  const sentinel = strategySentinelEl.value;
  const card = strategyCardEl.value;
  if (!sentinel || !card) return;
  const should = sentinel.getBoundingClientRect().top < STRATEGY_STICKY_TOP;
  if (should === isStrategyStuck) return;
  if (should) {
    // 即将 fixed，先用未固定时的几何位置算出 left/width，避免横向跳位
    const rect = card.getBoundingClientRect();
    strategyCardStyle.value = {
      position: "fixed",
      top: `${STRATEGY_STICKY_TOP}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    };
    isStrategyStuck = true;
  } else {
    strategyCardStyle.value = {};
    isStrategyStuck = false;
  }
};

const onScroll = () => updateSticky();
const onResize = () => {
  if (isStrategyStuck) {
    // 窗口尺寸变化会改变 left/width，先复位重新测量
    strategyCardStyle.value = {};
    isStrategyStuck = false;
    nextTick(updateSticky);
  } else {
    updateSticky();
  }
};

onMounted(async () => {
  await nextTick();
  // 主要监听 window；同时找到 overflow 的祖先（兜底，万一以后布局改成内部滚动）
  window.addEventListener("scroll", onScroll, { passive: true });
  let p = strategySentinelEl.value?.parentElement;
  while (p) {
    const ov = getComputedStyle(p).overflowY;
    if (ov === "auto" || ov === "scroll") {
      scrollParent = p;
      p.addEventListener("scroll", onScroll, { passive: true });
      break;
    }
    p = p.parentElement;
  }
  window.addEventListener("resize", onResize, { passive: true });
  updateSticky();

  /* 数据统一通路：query 带 id（上传后跳入或历史草稿进入都一样），
   * 拉 GetDetailById 回填 sourceData（驱动 contractFile / stanceOptions 重建）
   * + 已填过的审查参数（stance / scale / reviewer / customRequirement）。
   *
   * 拿到 fileId 后再调 getGetById 取文件元信息（文件名/字数/大小/类型…），
   * 合并回 sourceData 让合同文件框显示接口返回的真实信息。 */
  const id = route.query.id;
  if (id != null) {
    detailLoading.value = true;
    try {
      const res = await getGetDetailById(id);
      const data = (res && (res.data || res)) || {};
      const fileId = data.fileId || data.file_id || route.query.fileId || null;
      sourceData.value = normalizeDetail(data, id, fileId);
      // 后端存的是 stance 枚举（PartyA/PartyB），映射回第一张匹配的卡片 key 完成回显
      if (data.stance) {
        const opt = stanceOptions.value.find((o) => o.stance === data.stance);
        if (opt) selectedStance.value = opt.key;
      }
      if (data.scale) selectedScale.value = data.scale;
      // 合同状态回显（后端可能不返回该字段，仅在明确返回时覆盖默认值）
      if (data.isDraft !== undefined && data.isDraft !== null)
        isDraft.value = !!data.isDraft;
      if (data.reviewer) reviewer.value = data.reviewer;
      if (data.customRequirement)
        customRequirement.value = data.customRequirement;

      // 拉文件元信息合并到 sourceData，失败不阻塞配置页
      if (fileId != null) {
        try {
          const fileRes = await getGetById(fileId);
          const f = (fileRes && (fileRes.data || fileRes)) || {};
          if (Object.keys(f).length) {
            sourceData.value = {
              ...sourceData.value,
              // 字段做兜底兼容后端可能的不同命名
              name:
                f.fileName ||
                f.name ||
                f.originalFileName ||
                sourceData.value.name,
              contractType:
                f.contractType || f.fileType || sourceData.value.contractType,
              wordCount:
                f.wordCount || f.word_count || sourceData.value.wordCount,
              fileSize: f.fileSize || f.size || f.file_size || f.length,
              // fileId / contractId 已由 normalizeDetail 设置，保留
            };
          }
        } catch (err) {
          console.error("[ReviewRequirements] getGetById error:", err);
        }
      }

      // 参考文件列表：referenceFileIds 是逗号分隔的字符串，逐个调 getGetById 取文件信息
      const refIdsStr = data.referenceFileIds || "";
      const refIds = String(refIdsStr)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (refIds.length) {
        const results = await Promise.allSettled(
          refIds.map((rid) => getGetById(rid)),
        );
        referenceFiles.value = refIds
          .map((rid, idx) => {
            const r = results[idx];
            if (!r || r.status !== "fulfilled") return null;
            const f = (r.value && (r.value.data || r.value)) || {};
            const name =
              f.fileName || f.name || f.originalFileName || String(rid);
            return {
              fileId: rid,
              name,
              iconLetter: getGetByIdIconLetter(name),
            };
          })
          .filter(Boolean);
      }
    } catch (err) {
      console.error("[ReviewRequirements] getGetDetailById error:", err);
    } finally {
      detailLoading.value = false;
    }
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll);
  window.removeEventListener("resize", onResize);
  if (scrollParent) scrollParent.removeEventListener("scroll", onScroll);
});
</script>

<style lang="less" scoped>
.review-requirements {
  line-height: 1.5;
  margin-top: 24px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 24px;
  align-items: start;

  .review-requirements-header {
    grid-column: 1 / -1;
    margin-bottom: 0;
    .review-requirements-header-title {
      font-size: 34px;
      font-weight: 600;
      color: #12213f;
      letter-spacing: -0.6px;
    }
    .review-requirements-header-desc {
      font-size: 15px;
      color: #64748f;
      letter-spacing: -0.23px;
      margin-top: 4px;
    }
  }

  .config-card {
    background: #ffffff;
    border: 1px solid #e6ebf2;
    border-radius: 16px;
    box-shadow: 0 6px 18px rgba(31, 45, 61, 0.05);
    padding: 24px;
    margin-bottom: 0;
  }

  .card-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 16px;
    .card-row-left {
      display: flex;
      flex-direction: column;
      .card-row-label {
        font-weight: 600;
        font-size: 17px;
        color: #213554;
        letter-spacing: -0.43px;
      }
      .card-row-desc {
        font-size: 13px;
        color: #718096;
        margin-top: 4px;
      }
    }
    .right-tag {
      background: #edf4ff;
      border-radius: 6px;
      padding: 2px 10px;
      font-size: 12px;
      color: #1768ff;
      height: 24px;
      white-space: nowrap;
    }
  }

  .draft-status-row {
    // 与审查立场一致的上下布局：标题描述在上，选项整行在下
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
    .draft-status-copy {
      .section-title {
        font-weight: 600;
        font-size: 17px;
        color: #213554;
        letter-spacing: -0.43px;
      }
      .section-desc {
        font-size: 13px;
        color: #718096;
        margin-top: 4px;
      }
    }
    .draft-status-options {
      display: flex;
      gap: 12px;
      margin-top: 12px;
    }
    .draft-option {
      flex: 1;
      display: flex;
      align-items: flex-start;
      gap: 10px;
      border: 1px solid #e3eaf4;
      border-radius: 12px;
      background: #ffffff;
      padding: 14px 18px;
      cursor: pointer;
      transition: all 0.15s;
      text-align: left;
      font-family: inherit;
      line-height: 1.5;
      &:hover {
        border-color: #9bbdf7;
      }
      &.active {
        border-color: #9bbdf7;
        background: #f8fbff;
      }
      .draft-radio {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 1.5px solid #c4d0e2;
        background: #ffffff;
        flex-shrink: 0;
        margin-top: 2px;
        box-sizing: border-box;
        transition: all 0.15s;
      }
      &.active .draft-radio {
        border: 4px solid #1768ff;
      }
      .draft-option-content {
        display: flex;
        flex-direction: column;
        .draft-option-title {
          font-weight: 600;
          font-size: 14px;
          color: #253957;
        }
        .draft-option-desc {
          font-size: 12px;
          color: #78859a;
          margin-top: 2px;
        }
      }
    }
  }

  .file-box {
    background: #fbfdff;
    border: 1px solid #e8edf5;
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
    .file-icon {
      background: #eef4ff;
      width: 40px;
      height: 40px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #1768ff;
      font-weight: 600;
      font-size: 18px;
      flex-shrink: 0;
    }
    .file-info {
      flex: 1;
      min-width: 0;
      .file-name {
        font-weight: 600;
        color: #203452;
      }
      .file-meta {
        font-size: 12px;
        color: #8a96aa;
        margin-top: 2px;
      }
    }
    .file-status {
      background: #eaf8f2;
      color: #168760;
      padding: 2px 12px;
      border-radius: 20px;
      font-size: 12px;
      white-space: nowrap;
      flex-shrink: 0;
    }
  }

  .stance-group {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin: 12px 0 20px 0;
    .stance-item {
      // 一行最多 3 个（24px = 2 个 12px 间隙），超过换行；少于 3 个时自动均分当前行
      flex: 1 1 calc((100% - 24px) / 3);
      border: 1px solid #e3eaf4;
      border-radius: 12px;
      padding: 16px 18px;
      background: #ffffff;
      cursor: pointer;
      transition: all 0.15s;
      &.active {
        border-color: #9bbdf7;
        background: #f8fbff;
      }
      .stance-title {
        font-weight: 600;
        color: #1768ff;
        font-size: 13px;
      }
      .stance-company {
        font-weight: 600;
        font-size: 16px;
        color: #253957;
        margin: 6px 0 4px;
      }
      .stance-hint {
        font-size: 12px;
        color: #78859a;
      }
    }
  }

  .scale-group {
    display: flex;
    gap: 12px;
    margin: 12px 0 20px 0;
    .scale-item {
      flex: 1;
      border: 1px solid #e3eaf4;
      border-radius: 12px;
      padding: 16px 18px;
      background: #ffffff;
      cursor: pointer;
      transition: all 0.15s;
      &.active {
        border-color: #9bbdf7;
        background: #f8fbff;
      }
      .scale-title {
        font-weight: 600;
        color: #213554;
        font-size: 16px;
      }
      .scale-desc {
        font-size: 13px;
        color: #66758f;
        margin-top: 6px;
      }
    }
  }

  .section-block {
    margin-top: 20px;
    .section-label {
      font-weight: 600;
      font-size: 17px;
      color: #213554;
      display: block;
    }
    .section-desc {
      font-size: 13px;
      color: #718096;
      display: block;
      margin-top: 4px;
    }
    .input-label {
      font-size: 13px;
      color: #63718a;
      margin: 12px 0 6px 0;
      display: block;
      &.input-label-mt {
        margin-top: 16px;
      }
    }
  }

  .section-block-mt {
    margin-top: 24px;
  }

  .ref-box {
    background: #fafcff;
    border: 1px solid #c4d3e7;
    border-radius: 12px;
    padding: 16px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
    .ref-left {
      font-size: 16px;
      color: #61718a;
      .ref-hint {
        font-weight: 400;
        font-size: 13px;
        color: #8a96aa;
        margin-left: 4px;
      }
    }
  }

  .ref-list {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .ref-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background: #fbfdff;
    border: 1px solid #e8edf5;
    border-radius: 10px;
  }

  .ref-item .file-ico {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #eef4ff;
    color: #1768ff;
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .ref-item .file-main {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    color: #203452;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ref-item .icon-btn {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border: none;
    background: transparent;
    border-radius: 6px;
    color: #8a96aa;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;
  }

  .ref-item .icon-btn:hover {
    background: #fff1f2;
    color: #be123c;
  }

  .action-row {
    display: flex;
    justify-content: flex-end;
    gap: 14px;
    margin-top: 24px;
  }

  .strategy-cell {
    // grid item：占据右列；strategy-card 内部用 JS 切换为 fixed 时，sentinel 仍占据正常流，
    // 由于 config-card 是同一行的较高一侧，行高不受影响。
    display: flex;
    flex-direction: column;
  }

  .strategy-sentinel {
    height: 0;
    width: 100%;
    pointer-events: none;
  }

  .strategy-card {
    background: #ffffff;
    border: 1px solid #e6ebf2;
    border-radius: 16px;
    padding: 18px 24px 22px 24px;
    box-shadow: 0 6px 18px rgba(31, 45, 61, 0.05);
    .strategy-card-title {
      font-weight: 600;
      font-size: 15px;
      color: #1f2d3d;
      margin-bottom: 12px;
      &.strategy-card-title-mt {
        margin-top: 24px;
      }
    }
    .strategy-item {
      background: #fbfdff;
      border: 1px solid #e8edf5;
      border-radius: 12px;
      padding: 14px 18px;
      display: flex;
      align-items: center;
      gap: 14px;
      margin-bottom: 12px;
      .strategy-badge {
        background: #eef4ff;
        width: 40px;
        height: 40px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #1768ff;
        font-weight: 600;
        font-size: 18px;
        flex-shrink: 0;
      }
      .strategy-info {
        .strategy-main {
          font-weight: 600;
          color: #203452;
        }
        .strategy-sub {
          font-size: 12px;
          color: #8a96aa;
          margin-top: 2px;
        }
      }
    }
    .strategy-item.is-pending {
      background: #fbfbfc;
      border: 1px dashed #d3dae6;
      .strategy-badge {
        background: #f0f1f5;
        color: #9aa3b2;
      }
      .strategy-info .strategy-main {
        color: #8a96aa;
        font-weight: 500;
      }
    }
    .tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 10px;
      .tag {
        border-radius: 6px;
        padding: 4px 14px;
        font-size: 12px;
        &.tag-default {
          background: #edf4ff;
          color: #1768ff;
        }
        &.tag-risk {
          background: #fff1f2;
          color: #be123c;
        }
        &.tag-symbol {
          background: #f4edff;
          color: #6d28d9;
        }
        &.tag-party {
          background: #fff5e8;
          color: #b66f11;
        }
      }
    }
  }
}

:deep(.custom-input) {
  border-radius: 10px;
  &:hover,
  &:focus {
    border-color: #1768ff;
  }
}

:deep(.custom-textarea) {
  border-radius: 10px;
  &:hover,
  &:focus {
    border-color: #1768ff;
  }
}
</style>
